import React, { createContext, useContext, useState, useEffect } from 'react';
import { gql, useMutation, useApolloClient, useLazyQuery } from '@apollo/client';
import axios from 'axios';
import { UPDATE_PASSWORD_FIRST_TIME, RESET_FIRST_LOGIN } from '../graphql/mutations';
import { API_URL } from '../config';
import { GET_CURRENT_USER } from '../graphql/queries';
import { LOGIN, LOGOUT, RESET_PASSWORD, REFRESH_TOKEN } from '../graphql/mutations';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { setLoggingOut } from '../apollo-client';
import { 
  areCookiesAvailable, 
  getAuthErrorMessage, 
  retryWithBackoff, 
  delay, 
  formatTimeRemaining 
} from '../utils/tokenUtils';

// Define GraphQL queries/mutations
const ME_QUERY = gql`
  query Me {
    me {
      id
      email
      firstName
      lastName
      role
      isActive
      isFirstLogin
      phoneNumber
      avatarUrl
      areas
      smsNotifications
      emailNotifications
      notificationEmailOverride
      lastAuthMethod
    }
  }
`;

const AuthContext = createContext(null);

export const AuthProvider = ({ children, onError }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshTimer, setRefreshTimer] = useState(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false); // Add logout state
  const [sessionStatus, setSessionStatus] = useState({
    isActive: false,
    canRefresh: false,
    timeUntilExpiry: null,
    lastRefresh: null
  });
  const [authError, setAuthError] = useState(null);
  const navigate = useNavigate();
  const client = useApolloClient();
  const [loginMutation] = useMutation(LOGIN);
  const [logoutMutation] = useMutation(LOGOUT);
  const [resetPasswordMutation] = useMutation(RESET_PASSWORD);
  const [updatePasswordFirstTimeMutation] = useMutation(UPDATE_PASSWORD_FIRST_TIME);
  const [resetFirstLoginMutation] = useMutation(RESET_FIRST_LOGIN);
  const [getCurrentUser, { loading: userLoading }] = useLazyQuery(GET_CURRENT_USER, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (data?.me) {
        setUser(data.me);
      } else {
        setUser(null);
      }
      setLoading(false);
    },
    onError: (error) => {
      setUser(null);
      setLoading(false);
    }
  });
  const [refreshTokenMutation] = useMutation(REFRESH_TOKEN);

  // Configure axios defaults
  axios.defaults.withCredentials = true;

  // Handler for setting errors
  const handleError = (err) => {
    setAuthError(err);
    // Call external error handler if provided
    if (onError && typeof onError === 'function') {
      // Only propagate critical errors that would prevent the app from functioning
      if (err && (
        err.message?.includes('network') || 
        err.message?.includes('connection') ||
        err.message?.includes('failed to fetch') ||
        err.message?.includes('cannot load')
      )) {
        onError(err);
      }
    }
  };

  useEffect(() => {
    // Initialize authentication on component mount
    const initAuth = async () => {
      try {
        // Enhanced cookie availability check
        if (!areCookiesAvailable()) {
          const cookieError = new Error('Cookies must be enabled to use this application. Please enable cookies in your browser settings and refresh the page.');
          handleError(cookieError);
          setUser(null);
          setLoading(false);
          return;
        }

        // Check token status first for better UX
        try {
          const tokenStatusResponse = await axios.get(`${API_URL}/api/auth/token-status`, {
            withCredentials: true,
            timeout: 8000
          });

          const tokenStatus = tokenStatusResponse.data;

          // Update session status
          setSessionStatus({
            isActive: tokenStatus.isAuthenticated,
            canRefresh: tokenStatus.canRefresh,
            timeUntilExpiry: tokenStatus.timeUntilExpiry,
            lastRefresh: new Date().toISOString()
          });

          if (tokenStatus.isAuthenticated && tokenStatus.user) {
            setUser(tokenStatus.user);
            setAuthError(null);
            setLoading(false);
            return;
          }

          // Only attempt refresh if we explicitly have a refresh token
          if (tokenStatus.canRefresh && tokenStatus.hasRefreshToken) {
            const refreshSuccess = await refreshToken();
            if (refreshSuccess) {
              setLoading(false);
              return;
            }
          }

        } catch (tokenStatusError) {
          // Don't attempt fallback, just proceed to no-auth state
        }

        // No authentication found - this is normal for logged-out users
        setUser(null);
        setAuthError(null); // This is not an error condition, just no auth
        setLoading(false);

      } catch (error) {
        const friendlyError = getAuthErrorMessage(error);
        handleError(new Error(friendlyError));
        setUser(null);
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  // Try to refresh token if access token is expired
  const refreshToken = async () => {
    // Prevent multiple simultaneous refresh attempts
    if (refreshing) {
      return false;
    }
    
    try {
      setRefreshing(true);
      
      // Single refresh attempt - no retry for refresh tokens
      const response = await axios.post(`${API_URL}/api/auth/refresh`, {}, {
        withCredentials: true,
        timeout: 10000
      });
      
      if (response.status === 200 && response.data) {
        // Update session status
        setSessionStatus(prev => ({
          ...prev,
          isActive: true,
          lastRefresh: new Date().toISOString()
        }));
        
        // If user data is returned, update user state
        if (response.data.user) {
          setUser(response.data.user);
        }
        
        // Clear any previous auth errors
        setAuthError(null);
        
        return true;
      }
      
      return false;
      
    } catch (error) {
      // Handle specific error cases
      if (error.response?.status === 401) {
        setUser(null);
        setSessionStatus({
          isActive: false,
          canRefresh: false,
          timeUntilExpiry: null,
          lastRefresh: null
        });
        
        // Clear auth error to prevent showing login errors during auto-refresh
        setAuthError(null);
        
      } else if (error.response?.status === 429) {
        // Rate limited - back off
        setAuthError('Too many requests. Please wait a moment before trying again.');
        
      } else {
        // Handle other errors quietly for background refresh
        // Only set error if this was a user-initiated refresh
        if (!user) {
          const friendlyError = getAuthErrorMessage(error);
          setAuthError(friendlyError);
        }
      }
      
      return false;
    } finally {
      setRefreshing(false);
    }
  };

  // Set up refresh token timer to keep the session alive
  useEffect(() => {
    if (!user) return;
    
    // Single auto-refresh timer with proper guards
    const setupAutoRefresh = async () => {
      try {
        // Check token status to see if we have a refresh token
        const response = await axios.get(`${API_URL}/api/auth/token-status`, {
          withCredentials: true,
          timeout: 5000
        });
        
        const { canRefresh, hasRefreshToken } = response.data;
        
        if (canRefresh && hasRefreshToken) {
          // Single timer - refresh 15 minutes before 1-hour access token expires
          const smartRefreshInterval = 45 * 60 * 1000; // 45 minutes
          
          const smartRefreshTimer = setInterval(async () => {
            // Double-check we still have a refresh token before attempting
            try {
              const statusResponse = await axios.get(`${API_URL}/api/auth/token-status`, {
                withCredentials: true,
                timeout: 3000
              });
              
              if (statusResponse.data.canRefresh && statusResponse.data.hasRefreshToken) {
                const refreshSuccess = await refreshToken();
                if (!refreshSuccess) {
                  clearInterval(smartRefreshTimer);
                }
              } else {
                clearInterval(smartRefreshTimer);
              }
            } catch (error) {
              // Clear timer if we can't check status
              clearInterval(smartRefreshTimer);
            }
          }, smartRefreshInterval);
          
          // Return cleanup function
          return () => {
            clearInterval(smartRefreshTimer);
          };
        } else {
          return () => {}; // No cleanup needed
        }
      } catch (error) {
        return () => {}; // No cleanup needed
      }
    };
    
    // Set up auto-refresh and store cleanup function
    let cleanup;
    setupAutoRefresh().then(cleanupFn => {
      cleanup = cleanupFn;
    }).catch(error => {
      // Auto-refresh setup failed - continue without auto-refresh
    });
    
    // Return cleanup function
    return () => {
      if (cleanup) {
        cleanup();
      }
    };
  }, [user?.id]); // Only depend on user.id to prevent unnecessary re-runs

  const checkAuth = async () => {
    try {
      // Since HTTP-only cookies are automatically included in requests,
      // we don't need to check if the cookie exists in document.cookie
      
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        withCredentials: true,
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data) {
        setUser(response.data);
        setLoading(false);
        return response.data;
      } else {
        setUser(null);
        setLoading(false);
        return null;
      }
    } catch (error) {
      // Always make sure we clear loading state and user data on error
      setUser(null);
      setLoading(false);
      
      return null;
    }
  };

  const refreshUser = async () => {
    try {
      await getCurrentUser();
      return true;
    } catch (error) {
      return false;
    }
  };

  const login = async (email, password, rememberMe = false) => {
    try {
      // Pre-flight check for cookies
      if (!areCookiesAvailable()) {
        const cookieError = 'Cookies must be enabled to log in. Please enable cookies in your browser settings.';
        setAuthError(cookieError);
        return { success: false, error: cookieError };
      }
      
      // Clear any previous errors
      setAuthError(null);
      
      const response = await retryWithBackoff(async () => {
        return await axios.post(`${API_URL}/api/auth/login`, {
          email,
          password,
          rememberMe
        }, {
          withCredentials: true,
          timeout: 15000 // Longer timeout for login
        });
      }, 2, 1000);
      
      if ((response.status === 200 || response.status === 201) && response.data) {
        if (response.data.user) {
          // Update auth state with user data
          setUser(response.data.user);
          setAuthError(null);
          
          // Update session status
          setSessionStatus({
            isActive: true,
            canRefresh: rememberMe,
            timeUntilExpiry: response.data.expiresIn ? Date.now() + (response.data.expiresIn * 1000) : null,
            lastRefresh: new Date().toISOString()
          });
          
          return {
            success: true,
            user: response.data.user,
            ssoNotification: response.data.ssoNotification || null,
          };
        } else {
          const error = 'Invalid response from server. Please try again.';
          setAuthError(error);
          setUser(null);
          
          return { success: false, error };
        }
        
      } else {
        const error = 'Login failed. Please try again.';
        setAuthError(error);
        return { success: false, error };
      }
      
    } catch (err) {
      // Handle specific error cases with friendly messages
      if (err.response?.status === 401) {
        const error = 'Invalid email or password. Please check your credentials and try again.';
        setAuthError(error);
        
        return { success: false, error };
        
      } else if (err.response?.status === 429) {
        const error = 'Too many login attempts. Please wait a few minutes and try again.';
        setAuthError(error);
        
        return { success: false, error };
        
      } else if (err.response?.status >= 500) {
        const error = 'Server error. Please try again later.';
        setAuthError(error);
        
        return { success: false, error };
        
      } else {
        // Use friendly error message utility
        const friendlyError = getAuthErrorMessage(err);
        setAuthError(friendlyError);
        
        return { success: false, error: friendlyError };
      }
    }
  };

  const googleSsoLogin = async (credential) => {
    try {
      if (!areCookiesAvailable()) {
        const cookieError = 'Cookies must be enabled to log in. Please enable cookies in your browser settings.';
        setAuthError(cookieError);
        return { success: false, error: cookieError };
      }
      setAuthError(null);
      const response = await axios.post(`${API_URL}/api/auth/google-sso`, {
        credential
      }, {
        withCredentials: true,
        timeout: 15000
      });
      if ((response.status === 200 || response.status === 201) && response.data) {
        if (response.data.user) {
          setUser(response.data.user);
          setAuthError(null);
          setSessionStatus({
            isActive: true,
            canRefresh: true,
            timeUntilExpiry: null,
            lastRefresh: new Date().toISOString()
          });
          return {
            success: true,
            user: response.data.user,
            isFirstLogin: response.data.isFirstLogin,
            authMethod: 'google_sso',
          };
        }
        return { success: false, error: 'Invalid response from server' };
      }
      return { success: false, error: 'Google sign-in failed' };
    } catch (err) {
      if (err.response?.status === 401) {
        const errorMsg = err.response?.data?.message || 'No account found for this email. Please contact your administrator.';
        setAuthError(errorMsg);
        return { success: false, error: errorMsg };
      }
      const friendlyError = getAuthErrorMessage(err);
      setAuthError(friendlyError);
      return { success: false, error: friendlyError };
    }
  };

  const logout = async () => {
    try {
      // Set global logout flags immediately to prevent any new queries
      setIsLoggingOut(true);
      setLoggingOut(true);
      
      // Clear user state immediately to stop components from making queries
      setUser(null);
      
      // Clear any timers
      if (refreshTimer) {
        clearTimeout(refreshTimer);
        setRefreshTimer(null);
      }
      
      // Stop Apollo Client from making any new network requests
      await client.stop();
      
      // Clear the Apollo cache to prevent stale data
      await client.clearStore();
      
      // Now make the logout request to the server
      const response = await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        console.warn('Logout request failed, but continuing with client-side logout');
      }

      // Navigate to login page
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
      // Even if there's an error, ensure we navigate to login
      navigate('/login', { replace: true });
    } finally {
      // Reset logout flags after navigation
      setIsLoggingOut(false);
      setLoggingOut(false);
    }
  };

  const resetPassword = async (token, newPassword) => {
    try {
      const { data } = await resetPasswordMutation({
        variables: { 
          input: { 
            token, 
            newPassword 
          }
        }
      });
      return { success: !!data?.resetPassword };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const updatePasswordFirstTime = async ({ variables }) => {
    try {
      const { data } = await updatePasswordFirstTimeMutation({
        variables
      });
      
      if (data?.updatePasswordFirstTime) {
        // Check if profile is complete before trying to reset first login flag
        await refreshUser(); // Make sure we have latest user data
        
        const profileComplete = user && 
          user.firstName && 
          user.lastName && 
          user.phoneNumber && 
          user.avatarUrl;
        
        // Only try to reset first login if profile is complete
        if (profileComplete) {
          try {
            await resetFirstLoginMutation();
          } catch (resetError) {
            // Don't throw here - password was updated successfully
          }
        }
        
        await refreshUser();
        return { success: true };
      }
      
      return { success: false, error: 'Password update failed' };
    } catch (error) {
      throw error;
    }
  };

  const checkProfileCompletion = async () => {
    try {
      // Use the refreshed user data to determine completion
      await refreshUser();
      
      // If no user is logged in, return incomplete
      if (!user) {
        return { 
          profileComplete: false,
          missingFields: { all: true }
        };
      }
      
      // Check required fields
      const missingFields = {};
      
      if (!user.firstName) missingFields.firstName = true;
      if (!user.lastName) missingFields.lastName = true;
      if (user.isFirstLogin) missingFields.password = true;
      
      const profileComplete = Object.keys(missingFields).length === 0;
      
      return {
        profileComplete,
        missingFields,
      };
    } catch (error) {
      // Default to not complete on error
      return { 
        profileComplete: false, 
        error: error.message
      };
    }
  };

  // Set up axios interceptors for authentication
  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        // Ensure credentials are included for all requests
        config.withCredentials = true;
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // If the error is 401 and we haven't tried to refresh the token yet
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          // Don't try to refresh if we know there's no refresh token
          if (error.response?.data?.message?.includes('No refresh token found')) {
            return Promise.reject(error);
          }

          // Don't try to refresh if we're already refreshing
          if (refreshing) {
            return Promise.reject(error);
          }

          // Don't try to refresh for the refresh endpoint itself to prevent infinite loops
          if (originalRequest.url?.includes('/api/auth/refresh')) {
            return Promise.reject(error);
          }

          try {
            // Try to refresh the token
            const refreshed = await refreshToken();
            if (refreshed) {
              // Retry the original request
              return axios(originalRequest);
            } else {
              // Refresh failed, logout user
              await logout();
            }
          } catch (refreshError) {
            // Logout user on refresh failure
            await logout();
          }
        }

        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [refreshing]); // Add refreshing to dependency array

  // Function to check if cookies are working properly
  const verifyCookieSupport = async () => {
    try {
      // First, check if the browser reports cookies as enabled
      if (!navigator.cookieEnabled) {
        return false;
      }

      // Set a test cookie that is not HTTP-only to check basic cookie support
      document.cookie = "testcookie=1; path=/";
      const basicCookiesWork = document.cookie.includes('testcookie');
      
      if (!basicCookiesWork) {
        return false;
      }
      
      // For HTTP-only cookies, we need to make a real API call to test
      // But we don't want to make unnecessary API calls if the user isn't logged in
      // For now, just return true if basic cookies work
      return true;
    } catch (error) {
      return false;
    }
  };

  useEffect(() => {
    // Check cookie support on component mount
    verifyCookieSupport().then(supported => {
      // Cookie support check completed
    });
  }, []);

  const value = {
    user,
    loading,
    error: authError,
    refreshing,
    isLoggingOut,
    sessionStatus,
    login,
    googleSsoLogin,
    logout,
    refreshToken,
    timeUntilRefresh: refreshTimer ? Math.max(0, Math.floor((refreshTimer - Date.now()) / 1000)) : null,
    updatePasswordFirstTime,
    refreshUser,
    checkProfileCompletion,
    checkAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};